/**
 * "More for this job" on the new job page (threads/job-page-fresh): every
 * rare thing, in a few named groups, one tap away. Nothing on the classic page
 * is lost: `FRESH_COVERS` maps every row of the classic page's action list
 * (`jobActions.classic.ts`, 68 rows) to where it lives on this page — a part of
 * the page, a More item that does it here, or a More item that opens the
 * classic page for this job (`?page=classic`, without touching the switch).
 * `fresh-more.test.ts` fails if a classic row has no home.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { CancelJobDialog } from "@/components/cancel-job-dialog";
import { ReopenJobDialog } from "@/components/reopen-job-dialog";
import { SendBackToFieldDialog } from "@/components/send-back-to-field-dialog";
import { TaskCreateDialog } from "@/components/task-create-dialog";
import { ResendPacketDialog } from "@/components/resend-packet-dialog";
import { useSendHistory } from "@/lib/use-send-history";
import { logPageEscape } from "@/lib/page-escape";
import type { FinishLine } from "@/lib/finish-line";
import { packetSubjectFor } from "../jobDetailHelpers";
import type { JobDetail } from "../detail/types";
import { closeoutLink } from "./freshModel";

/** Where each classic-page action lives on the new page. */
export type FreshHome = { on: "page"; where: string } | { on: "more"; item: MoreItemId };

export type MoreItemId =
    | "edit-job"
    | "customer"
    | "site"
    | "contacts"
    | "classic-history"
    | "field-workflow"
    | "closeout-documents"
    | "closeout-charges"
    | "closeout-estimate"
    | "closeout-payment"
    | "closeout-customer"
    | "portal"
    | "new-invoice"
    | "new-estimate"
    | "task"
    | "resend"
    | "send-back"
    | "reopen"
    | "cancel"
    | "checklist"
    | "rules";

export const FRESH_COVERS: Record<string, FreshHome> = {
    "title-copy": { on: "page", where: "header: job number copies; Edit job" },
    facts: { on: "page", where: "header: customer, site, street address" },
    lineage: { on: "page", where: "header: estimate and invoice numbers" },
    "stage-strip": { on: "page", where: "trail" },
    "status-forward": { on: "page", where: "header: the two status buttons and Move to" },
    "review-approve": { on: "page", where: "whose turn button → Closeout" },
    "send-back-reopen-cancel": { on: "page", where: "header: Move to (also in More)" },
    "create-invoice": { on: "more", item: "new-invoice" },
    "estimate-form-task": { on: "more", item: "task" },
    "resend-packet": { on: "more", item: "resend" },
    "task-flags": { on: "page", where: "strips under the header: task flags" },
    "service-hold": { on: "page", where: "whose turn: Do not service" },
    "checkup-strip": { on: "page", where: "strips under the header: Help Desk check-up" },
    "what-needs-you": { on: "page", where: "whose turn + trail" },
    "device-count-warning": { on: "page", where: "did it pass?" },
    "top-contact": { on: "page", where: "who to call" },
    "manage-contacts": { on: "more", item: "contacts" },
    "site-name-city": { on: "page", where: "header" },
    "all-contacts": { on: "page", where: "who to call: All contacts" },
    "see-visit": { on: "page", where: "when & who" },
    "change-date-tech": { on: "page", where: "schedule & crew: Edit (or Change date, tech or crew)" },
    crew: { on: "page", where: "schedule & crew: add or remove a tech or helper" },
    "confirm-booking": { on: "page", where: "schedule & crew: Confirm booking" },
    "another-visit": { on: "page", where: "schedule & crew: add another visit, cannot-complete" },
    "visit-history": { on: "more", item: "classic-history" },
    "tech-brief": { on: "page", where: "this is what the tech sees" },
    "details-core": { on: "page", where: "job details: Edit (type, window, due by, PO, WO)" },
    "tags-helper": { on: "page", where: "job details: tags, helper value" },
    ai: { on: "page", where: "job details: AI summary, AI estimate lines" },
    "summary-scope": { on: "page", where: "this is what the tech sees: Edit" },
    "wo-mapping": { on: "page", where: "work order mapping card" },
    "more-about": { on: "more", item: "classic-history" },
    "service-type": { on: "more", item: "edit-job" },
    "device-list": { on: "page", where: "devices" },
    "edit-device": { on: "page", where: "devices: the pencil on the device" },
    "remove-device": { on: "page", where: "devices: the trash can on the device" },
    "add-reconcile-workflow": { on: "page", where: "devices: Add device, Reconcile from list, Open the tech workflow" },
    "visit-kind": { on: "page", where: "devices: Test / Repair / Replace on the device" },
    "repair-paper": { on: "page", where: "devices: Use the signed paper, Record repair, Remove repair" },
    "fail-protocol": { on: "page", where: "devices: the failed device's fail protocol" },
    "quoted-devices": { on: "page", where: "devices: Add the quoted devices note" },
    "from-location": { on: "page", where: "devices: came-from-the-location note" },
    "other-site": { on: "page", where: "devices: other-site line on the device" },
    "orphan-protocols": { on: "page", where: "devices: protocols for devices no longer on the job" },
    upload: { on: "page", where: "paperwork: Upload (a test report files itself to its device)" },
    "city-filing": { on: "page", where: "paperwork: city filing summary and cheat sheet" },
    "office-files": { on: "page", where: "paperwork: office files" },
    "all-paperwork": { on: "page", where: "paperwork" },
    "official-remove-delete": { on: "page", where: "paperwork: make official, remove report, delete" },
    "zip-regenerate": { on: "page", where: "paperwork: select, zip, regenerate" },
    "wo-template-regen": { on: "page", where: "paperwork: Fix template, Regenerate WO PDF" },
    "use-completed-wo": { on: "page", where: "paperwork: held packet, Use as completed WO" },
    "packet-sent": { on: "page", where: "paperwork: packet sent, who didn't get it, Resend" },
    "money-rows": { on: "page", where: "paperwork: invoice and estimate rows with Send" },
    "money-create": { on: "more", item: "new-estimate" },
    po: { on: "page", where: "purchase order card" },
    parts: { on: "page", where: "parts card" },
    "water-authority": { on: "page", where: "water reporting card" },
    flag: { on: "page", where: "water reporting card: Waiting on tester, City hold" },
    "st-code": { on: "more", item: "classic-history" },
    permits: { on: "page", where: "permit card" },
    registration: { on: "page", where: "tester registration card" },
    compose: { on: "page", where: "notes & files: + Note; who to call: Text / Email" },
    feed: { on: "page", where: "notes & files: Show everything" },
    "pinned-note": { on: "page", where: "notes & files" },
    "job-photos": { on: "page", where: "photos card (Photos in notes & files jumps to it)" },
    "device-photos": { on: "page", where: "devices: tap a device" },
    help: { on: "page", where: "ⓘ on every box; Help Desk in the top bar" },
};

/**
 * Statuses with no packet history to ask about — the same list the classic
 * page uses before it reads send history (`detail.tsx`, `canResendPacket`).
 */
const NO_PACKET_YET = ["draft", "awaiting_scheduling", "unqueued", "cancelled"];

/** Ask the server for send history only when the job could have one. */
export function mayHaveSentPacket(status: string): boolean {
    return !NO_PACKET_YET.includes(status);
}

/**
 * "Resend the packet" shows only after a FIRST send, exactly like classic.
 * The first send approves the job, issues the draft invoice and can charge a
 * card on file; it lives in Closeout behind those gates. `everSent` is the
 * server's answer, never the job status. Unknown (still loading, or the read
 * failed) means hidden.
 */
export function canResendPacket(opts: { isOffice: boolean; everSent: boolean | undefined }): boolean {
    return opts.isOffice && opts.everSent === true;
}

/** A More item that leaves this page for the classic one (logged on use). */
export function isClassicEscape(i: { to?: string }): boolean {
    return !!i.to && i.to.includes("page=classic");
}

interface Item {
    id: MoreItemId;
    label: string;
    to?: string;
    href?: string;
    run?: () => void;
    hidden?: boolean;
    destructive?: boolean;
}

export function FreshMore({ job }: { job: JobDetail }) {
    const { isAdmin, isOffice, can } = useAuth();
    const sendHistoryQ = useSendHistory(job.id, isOffice && mayHaveSentPacket(job.status));
    const resendable = canResendPacket({ isOffice, everSent: sendHistoryQ.data?.everSent });
    const [open, setOpen] = useState(false);
    const [dialog, setDialog] = useState<null | "cancel" | "reopen" | "send-back" | "task" | "resend">(null);
    const [checklist, setChecklist] = useState(false);
    const classic = (hash: string) => `/jobs/${job.id}?page=classic${hash}`;
    const jobLabel = `Job #${job.display_number || job.id.slice(0, 8)}`;
    const doc = (kind: "estimate" | "invoice") =>
        `/commercial/new?jobId=${job.id}&customerId=${job.customer_id ?? ""}${job.site_id ? `&siteId=${job.site_id}` : ""}&kind=${kind}`;
    const closed = ["closed", "cancelled", "completed"].includes(job.status);
    const groups: Array<{ name: string; items: Item[] }> = [
        {
            name: "Visit",
            items: [
                {
                    id: "field-workflow",
                    label: "Open the tech's screens for this job",
                    to: `/field/backflow/${job.id}`,
                    hidden: job.trade_code !== "backflow" || closed,
                },
                { id: "classic-history", label: "Visit history and everything that happened", to: classic("") },
            ],
        },
        {
            name: "Job",
            items: [
                { id: "edit-job", label: "Edit job details (type, due date, PO, tags)", to: `/jobs/${job.id}/edit` },
                { id: "customer", label: `Open ${job.company_name}`, to: `/customers/${job.customer_id}` },
                {
                    id: "site",
                    label: "Open the site",
                    to: job.site_id ? `/sites/${job.site_id}` : undefined,
                    hidden: !job.site_id,
                },
                { id: "contacts", label: "All contacts", to: `/customers/${job.customer_id}?tab=contacts` },
                { id: "task", label: "+ Task (hand it to someone)", run: () => setDialog("task") },
                {
                    id: "send-back",
                    label: "Send back to the field",
                    run: () => setDialog("send-back"),
                    hidden: job.status !== "office_review",
                },
                { id: "reopen", label: "Reopen the job", run: () => setDialog("reopen"), hidden: !closed },
                {
                    id: "cancel",
                    label: "Cancel the job…",
                    run: () => setDialog("cancel"),
                    hidden: job.status === "cancelled",
                    destructive: true,
                },
            ],
        },
        {
            name: "Paperwork",
            items: [
                {
                    id: "closeout-documents",
                    label: "Reports and the work order (Closeout)",
                    to: closeoutLink(job.id, "reports"),
                },
                {
                    id: "portal",
                    label: "City portal",
                    href: job.water_provider_portal_url ?? undefined,
                    hidden: !job.water_provider_portal_url,
                },
                {
                    id: "resend",
                    label: "Resend the packet",
                    run: () => setDialog("resend"),
                    hidden: !resendable,
                },
            ],
        },
        {
            name: "Money",
            items: [
                {
                    id: "closeout-charges",
                    label: "Charges and the invoice (Closeout)",
                    to: closeoutLink(job.id, "invoice"),
                },
                { id: "closeout-estimate", label: "The quote (Closeout)", to: closeoutLink(job.id, "quote") },
                { id: "closeout-payment", label: "Take a payment (Closeout)", to: closeoutLink(job.id, "payment") },
                {
                    id: "closeout-customer",
                    label: "Send to the customer (Closeout)",
                    to: closeoutLink(job.id, "packet"),
                },
                { id: "new-invoice", label: "+ Invoice", to: doc("invoice") },
                { id: "new-estimate", label: "+ Estimate", to: doc("estimate") },
            ],
        },
    ];
    const owner = isAdmin || can("office_review.manage");
    if (isAdmin) {
        groups.push({
            name: "Corbin",
            items: [
                { id: "checklist", label: "Checklist: every step on this job", run: () => setChecklist((c) => !c) },
                { id: "rules", label: "Rules for jobs like this", to: `/customers/${job.customer_id}?tab=rules` },
            ],
        });
    }
    return (
        <section
            className="rounded-2xl border border-border bg-card px-4 py-3 text-card-foreground"
            data-testid="fresh-more"
        >
            <button
                type="button"
                className="flex min-h-[44px] w-full items-center justify-between text-left text-[17px] font-bold"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                More for this job
                <span aria-hidden>{open ? "▴" : "▾"}</span>
            </button>
            {open && (
                <div className="mt-2 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="fresh-more-groups">
                    {groups.map((g) => (
                        <div key={g.name}>
                            <h3 className="m-0 mb-1 text-[15px] font-extrabold">{g.name}</h3>
                            <ul className="m-0 grid list-none gap-0.5 p-0">
                                {g.items
                                    .filter((i) => !i.hidden)
                                    .map((i) => (
                                        <li key={i.id}>
                                            {i.to ? (
                                                <Link
                                                    to={i.to}
                                                    className={cn(ROW, i.destructive && DANGER)}
                                                    data-testid={`more-${i.id}`}
                                                    onClick={
                                                        isClassicEscape(i)
                                                            ? () => logPageEscape("job_page_fresh", i.id, job.id)
                                                            : undefined
                                                    }
                                                >
                                                    {i.label}
                                                </Link>
                                            ) : i.href ? (
                                                <a
                                                    href={i.href}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={ROW}
                                                    data-testid={`more-${i.id}`}
                                                >
                                                    {i.label} ↗
                                                </a>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className={cn(ROW, "w-full text-left", i.destructive && DANGER)}
                                                    onClick={i.run}
                                                    data-testid={`more-${i.id}`}
                                                >
                                                    {i.label}
                                                </button>
                                            )}
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
            {checklist && owner && <Checklist jobId={job.id} />}
            <CancelJobDialog
                open={dialog === "cancel"}
                onClose={() => setDialog(null)}
                jobId={job.id}
                jobLabel={jobLabel}
            />
            <ReopenJobDialog
                open={dialog === "reopen"}
                onClose={() => setDialog(null)}
                jobId={job.id}
                jobLabel={jobLabel}
            />
            <SendBackToFieldDialog
                open={dialog === "send-back"}
                onClose={() => setDialog(null)}
                jobId={job.id}
                jobLabel={jobLabel}
                techName={job.assigned_to_name ?? null}
            />
            <TaskCreateDialog
                open={dialog === "task"}
                onOpenChange={(o) => !o && setDialog(null)}
                prefill={{
                    title: `${jobLabel} — `,
                    links: [
                        { entityType: "job", entityId: job.id, label: jobLabel },
                        ...(job.customer_id
                            ? [
                                  {
                                      entityType: "customer",
                                      entityId: job.customer_id,
                                      label: job.company_name ?? "Customer",
                                  },
                              ]
                            : []),
                    ],
                }}
            />
            {dialog === "resend" && resendable && (
                <ResendPacketDialog
                    open
                    onClose={() => setDialog(null)}
                    jobId={job.id}
                    customerId={job.customer_id ?? null}
                    defaultSubject={packetSubjectFor(job)}
                    startInMissedMode={false}
                    onSent={() => setDialog(null)}
                    onGoToPaperwork={() => setDialog(null)}
                />
            )}
        </section>
    );
}

const ROW = "flex min-h-[40px] items-center rounded-md px-2 text-[15px] text-foreground hover:bg-muted";
const DANGER = "text-red-700 dark:text-red-400";

/** Corbin's checklist: the Finish Line's promises, read-only, in plain words. */
function Checklist({ jobId }: { jobId: string }) {
    const q = useQuery({
        queryKey: ["job", jobId, "finish-line"],
        queryFn: async () => (await api.get<{ finishLine: FinishLine }>(`/api/jobs/${jobId}/finish-line`)).finishLine,
        retry: false,
    });
    if (q.isLoading) return <p className="m-0 mt-2 text-muted-foreground">Loading…</p>;
    if (!q.data) return <p className="m-0 mt-2 text-muted-foreground">The checklist couldn't be loaded.</p>;
    const mark: Record<string, string> = { kept: "✓", open: "!", blocked: "✕", waiting: "◷", missed: "✕", later: "○" };
    return (
        <ul
            className="m-0 mt-3 grid list-none gap-1 border-t border-border p-0 pt-2 text-[14.5px]"
            data-testid="fresh-checklist"
        >
            {q.data.promises
                .filter((p) => p.state !== "not_applicable")
                .map((p) => (
                    <li key={p.id} className="flex gap-2">
                        <span className="w-5 text-center font-bold" aria-hidden>
                            {mark[p.state] ?? "·"}
                        </span>
                        <span>{p.title}</span>
                    </li>
                ))}
        </ul>
    );
}
